import type { StateCreator } from 'zustand';
import { type AppStore } from '../store';
import type {
  BeneficiaryType,
  NamedCompanyOrCommunityBeneficiary,
  NamedPersonBeneficiary,
} from 'dto/beneficiary';

/**
 * Get initial state for Beneficiary slice
 * @returns Initial beneficiary state with default values
 */
export const getInitialBeneficiaryState = () => ({
  selectedBeneficiary: undefined as BeneficiaryType | string | undefined,
  namedBeneficiaries: [] as Array<
    NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  >,
  maritalRightsExclusion: false,
});

/**
 * Beneficiary Slice - Manages beneficiary selection and configuration
 *
 * State:
 * - selectedBeneficiary: Currently selected beneficiary type or identifier
 * - namedBeneficiaries: List of named beneficiaries (persons/companies/communities)
 * - maritalRightsExclusion: Whether marital rights are excluded
 *
 * Actions:
 * - setSelectedBeneficiary: Update selected beneficiary
 * - setNamedBeneficiaries: Update entire list of named beneficiaries
 * - addNamedBeneficiary: Add a new named beneficiary
 * - updateNamedBeneficiary: Update beneficiary at specific index
 * - removeNamedBeneficiary: Remove beneficiary at specific index
 * - setMaritalRightsExclusion: Toggle marital rights exclusion
 */
export interface BeneficiarySlice {
  // State
  selectedBeneficiary: BeneficiaryType | string | undefined;
  namedBeneficiaries: Array<
    NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  >;
  maritalRightsExclusion: boolean;

  // Actions
  setSelectedBeneficiary: (
    beneficiary: BeneficiaryType | string | undefined
  ) => void;
  setNamedBeneficiaries: (
    beneficiaries: Array<
      NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
    >
  ) => void;
  addNamedBeneficiary: (
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ) => void;
  updateNamedBeneficiary: (
    index: number,
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ) => void;
  removeNamedBeneficiary: (index: number) => void;
  setMaritalRightsExclusion: (exclusion: boolean) => void;
}

/**
 * Creates the beneficiary slice
 */
export const createBeneficiarySlice: StateCreator<
  AppStore,
  [['zustand/devtools', never]],
  [],
  BeneficiarySlice
> = (set) => ({
  // Initial state
  ...getInitialBeneficiaryState(),

  // Actions
  setSelectedBeneficiary: (beneficiary) => {
    set(
      { selectedBeneficiary: beneficiary },
      false,
      'beneficiarySlice/setSelectedBeneficiary'
    );
  },

  setNamedBeneficiaries: (beneficiaries) => {
    set(
      { namedBeneficiaries: beneficiaries },
      false,
      'beneficiarySlice/setNamedBeneficiaries'
    );
  },

  addNamedBeneficiary: (beneficiary) => {
    set(
      (state) => ({
        namedBeneficiaries: [...state.namedBeneficiaries, beneficiary],
      }),
      false,
      'beneficiarySlice/addNamedBeneficiary'
    );
  },

  updateNamedBeneficiary: (index, beneficiary) => {
    set(
      (state) => ({
        namedBeneficiaries: state.namedBeneficiaries.map((b, i) =>
          i === index ? beneficiary : b
        ),
      }),
      false,
      'beneficiarySlice/updateNamedBeneficiary'
    );
  },

  removeNamedBeneficiary: (index) => {
    set(
      (state) => ({
        namedBeneficiaries: state.namedBeneficiaries.filter(
          (_, i) => i !== index
        ),
      }),
      false,
      'beneficiarySlice/removeNamedBeneficiary'
    );
  },

  setMaritalRightsExclusion: (exclusion) => {
    set(
      { maritalRightsExclusion: exclusion },
      false,
      'beneficiarySlice/setMaritalRightsExclusion'
    );
  },
});
